/**
 * Product name.
 *
 * Served by the API at runtime from APP_NAME, so renaming the system is a change to
 * .env and a restart — not a rebuild and redeploy. VITE_APP_NAME is only the value
 * shown for the fraction of a second before the config request returns, and the
 * fallback if the API is unreachable.
 *
 * On the name itself: this system covers the whole cycle — request, approval,
 * purchase order, goods receipt, invoice matching, export, payment. "Requisition"
 * names only the first step, so it undersells the financial controls that make up
 * the rest. "Procurement" is both accurate and the term finance teams already use.
 */

import { useEffect, useState } from 'react';

export interface PublicConfig {
  appName: string;
  ssoEnabled: boolean;
}

const FALLBACK: PublicConfig = {
  appName: import.meta.env.VITE_APP_NAME || 'Procurement',
  ssoEnabled: false,
};

/** Fetched once per page load and shared by every caller. */
let cached: PublicConfig | null = null;
let inFlight: Promise<PublicConfig> | null = null;

async function load(): Promise<PublicConfig> {
  if (cached) return cached;
  if (!inFlight) {
    inFlight = fetch(`${(import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')}/api/public-config`)
      .then((r) => (r.ok ? r.json() : FALLBACK))
      .then((value: PublicConfig) => {
        cached = { appName: value.appName || FALLBACK.appName, ssoEnabled: Boolean(value.ssoEnabled) };
        return cached;
      })
      // An unreachable API must not leave the sign-in page nameless.
      .catch(() => FALLBACK);
  }
  return inFlight;
}

export function usePublicConfig(): PublicConfig {
  const [value, setValue] = useState<PublicConfig>(cached ?? FALLBACK);

  useEffect(() => {
    let active = true;
    void load().then((loaded) => {
      if (active) setValue(loaded);
    });
    return () => {
      active = false;
    };
  }, []);

  return value;
}

export function useAppName(): string {
  const { appName } = usePublicConfig();

  // Keep the browser tab in step with the configured name.
  useEffect(() => {
    if (appName) document.title = appName;
  }, [appName]);

  return appName;
}

export const APP_TAGLINE = 'Purchase requests, approvals and invoices in one place.';
